import type {ReactElement, ReactNode} from 'react';
import React, {useEffect, useState} from 'react';
import {BLOGGER_TESTIMONY_INDEX, isMobileScreen, MUSIC_PROMOTER_TESTIMONY_INDEX, PRINCIPAL_TESTIMONY_INDEX, redirectToPage} from '@Libraries/login-signup-library';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {getAssetUrl} from '@Utils/s3.util';
import {LoginSignupPageAssetName} from '@Components/login-page/login-page.types';
import styles from './login-signup-testimony.module.scss';
import {Text} from '../text';
import {PostermywallNavigationLogo} from '../postermywall-navigation-logo';
import {TestimonySlideButtons} from './components/testimony-slide-buttons';
import {TestimonyTransition} from './components/testimony-transition';
import {EventPromotionLink} from '@Components/login-signup-testimony/components/event-promotion-link';
import useWindowSize from '@Hooks/useWindowSize';
import {NewFeatureBadge} from '@Components/login-signup-testimony/components/new-feature-badge/new-feature-badge';
import {NewFeatureBadgeColorType} from '@Components/login-signup-testimony/components/new-feature-badge/new-feature-badge.types';
import {AiChatLoginSection} from '@Components/login-signup-testimony/components/ai-chat-login-section/ai-chat-login-section';
import ClickableDiv from '@Components/clickable-div/clickable-div';

const TOTAL_TESTIMONIES = 3;
const TIME_DELAY_FOR_AUTOMATIC_TESTIMONY_CHANGE = 6000;

interface LoginSignupTestimonyProps {
  showTestimonies: boolean;
  doesReferrerExist: boolean;
}

function LoginSignupTestimony({showTestimonies, doesReferrerExist}: LoginSignupTestimonyProps): ReactElement<any> {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [doesUserControlAnimation, setDoesUsercontrolAnimation] = useState(false);
  const windowScreenSize = useWindowSize();

  useEffect(() => {
    const root = document.documentElement;
    root?.style.setProperty('--background-image', `url('${getAssetUrl(LoginSignupPageAssetName.BACKGROUND_TESTIMONY_SHAPES)}')`);

    root?.style.setProperty('--background-image-transition', `url('${getAssetUrl(LoginSignupPageAssetName.BACKGROUD_TESTIMONY_SHAPES_TRANSITION)}')`);
  }, []);

  useEffect(() => {
    if (doesUserControlAnimation) {
      return;
    }

    if (!isMobileScreen(windowScreenSize.windowWidth)) {
      return;
    }

    const timer = setInterval(() => {
      setCurrentIndex((currentIndex) => {
        return getNewIndex(currentIndex);
      });
    }, TIME_DELAY_FOR_AUTOMATIC_TESTIMONY_CHANGE);
    return (): void => {
      clearInterval(timer);
    };
  }, [doesUserControlAnimation]);

  const getEventsImageSource = (): string => {
    const imgNumber = Math.floor(Math.random() * 3);
    let imgSrc = getAssetUrl('login-page-event-promotion-image1.webp');

    if (imgNumber === 1) {
      imgSrc = getAssetUrl('login-page-event-promotion-image2.webp');
    }

    if (imgNumber === 2) {
      imgSrc = getAssetUrl('login-page-event-promotion-image3.webp');
    }

    return imgSrc;
  };

  const getNewIndex = (testimonyIndex: number): number => {
    if (testimonyIndex === TOTAL_TESTIMONIES - 1) {
      return 0;
    }
    return testimonyIndex + 1;
  };

  const updateCarousel = (): void => {
    if (doesUserControlAnimation) {
      setCurrentIndex(getNewIndex(currentIndex));
    } else {
      setDoesUsercontrolAnimation(true);
    }
  };

  const onSlideButtonClick = (e: React.MouseEvent, newIndex: number): void => {
    e.stopPropagation();
    setCurrentIndex(newIndex);
  };

  const getClassForTestimony = (testimonyIndex: number): string => {
    if (testimonyIndex === currentIndex) {
      return styles.fadeInTestimony;
    }
    return styles.fadeOutTestimony;
  };

  const getTestimonies = (): ReactNode => {
    if (!showTestimonies) {
      return <></>;
    }

    return (
      <>
        <div className={styles.nonLogoItemsContainer}>
          <div className={`${styles.headingContainer} flexbox flex-items-center`}>
            <Text val={window.i18next.t('pmwjs_login_page_marketing_welcome')} className={styles.heading} />
          </div>

          <div className={styles.transitionContainer}>
            <div className={getClassForTestimony(MUSIC_PROMOTER_TESTIMONY_INDEX)}>
              <TestimonyTransition index={MUSIC_PROMOTER_TESTIMONY_INDEX} />
            </div>
            <div className={getClassForTestimony(BLOGGER_TESTIMONY_INDEX)}>
              <TestimonyTransition index={BLOGGER_TESTIMONY_INDEX} />
            </div>
            <div className={getClassForTestimony(PRINCIPAL_TESTIMONY_INDEX)}>
              <TestimonyTransition index={PRINCIPAL_TESTIMONY_INDEX} />
            </div>
          </div>
        </div>
        <div className="flex-row-justify-center">
          <TestimonySlideButtons index={currentIndex} totalTestimonies={TOTAL_TESTIMONIES} onClick={onSlideButtonClick} />
        </div>
      </>
    );
  };

  const showBackIcon = (): boolean => {
    return doesReferrerExist && showTestimonies && !window.PMW.util.doesUrlContainEmbeddedEditorAuthenticationLayoverUri(window.location.href);
  };

  const onBackIconClick = (): void => {
    redirectToPage(window.PMW.util.homepage_url());
  };

  const getBackIcon = (iconType: IconType = IconType.NONE): ReactNode | null => {
    if (!showBackIcon()) {
      return null;
    }

    return (
      <Icon
        icon="icon-arrow-left"
        type={iconType}
        shape={IconShape.SQUARE}
        size={IconSize.SIZE_ICON_20}
        className={`spacing-m-t-4 spacing-m-l-4 ${styles.backIcon}`}
        onClick={onBackIconClick}
      />
    );
  };

  const getLogoClassNames = (): string => {
    const classNames = styles.logo;
    return showBackIcon() ? `${classNames} ${styles.iconVisible}` : classNames;
  };

  const getEventPromotionSection = (): ReactNode => {
    return (
      <div className={`flex-v-row flex-center ${styles.eventPromotionContent}`}>
        <NewFeatureBadge colorType={NewFeatureBadgeColorType.SECONDARY} className={styles.eventPill} />
        <Text val={window.i18next.t('pmwjs_introducing_hassle_free')} className={`${styles.heading} content-body-white`} bold={true} />
        <Text val={window.i18next.t('pmwjs_event_promotion')} className={`${styles.remainingHeading} ${styles.eventHeadingRemaining} content-body bg-neutral-0 spacing-m-t-3`} bold={true} />
      </div>
    );
  };

  const getDefaultLoginPageContent = (): ReactNode => {
    return (
      <div className={`${styles.container} flex-v-row`}>
        <div className={styles.backgroundContainer}>
          <div className={styles.backgroundOverlay} />
        </div>

        <ClickableDiv className={`flex-column-justify-center ${styles.contentContainer}`} onClick={updateCarousel}>
          <div>
            {getBackIcon()}
            <PostermywallNavigationLogo imageUrl={window.PMW.util.asset_url('images/logo.png')} containerClassName={getLogoClassNames()} imageClassName={styles.logoImg} />
          </div>

          {getTestimonies()}
        </ClickableDiv>
      </div>
    );
  };

  const getEventsPromotionImage = (): ReactNode => {
    const img = getEventsImageSource();
    let imgStyles = styles.eventImage;

    if (img.includes('3')) {
      imgStyles = imgStyles + ' ' + styles.eventImageThird;
    }

    return (
      <picture className={`flexbox flex-vertical-end ${styles.eventImageContainer}`}>
        <img src={img} className={imgStyles} alt="PosterMyWall events" />
      </picture>
    );
  };

  const getLogoContainer = (iconType: IconType = IconType.NONE): ReactElement => {
    return (
      <div>
        {getBackIcon(iconType)}
        <PostermywallNavigationLogo imageUrl={getAssetUrl('postermywall-login-page-logo.png')} containerClassName={getLogoClassNames()} imageClassName={styles.logoImg} />
      </div>
    );
  };

  const getEventsLoginPageContent = (): ReactNode => {
    return (
      <div className={`${styles.eventContainer} flex-v-row`}>
        <div className={styles.eventsBackgroundContainer}>
          <picture>
            <source srcSet={getAssetUrl('login-page-event-promotion-background-overlay.webp')} />
            <img src={getAssetUrl('login-page-event-promotion-background-overlay.png')} alt={'PosterMyWall events background'} className={styles.eventsBackgroundOverlay} />
          </picture>
        </div>

        <div className={`flex-column-justify-center ${styles.contentContainer}`}>
          {getLogoContainer()}

          <EventPromotionLink />

          <div className={`flex-v-row _full-height ${styles.eventPromotion}`}>
            {getEventPromotionSection()}
            {getEventsPromotionImage()}
          </div>
        </div>
      </div>
    );
  };

  const getAIChatLoginPageContent = (): ReactNode => {
    return (
      <div className={`bg-ai-dark-new-reversed flex-v-row _full-height ${styles.contentContainer} _overflow-hidden`}>
        {getLogoContainer(IconType.DARK_GHOST)}
        <AiChatLoginSection  textClassName={styles.eventHeading} />
      </div>
    );
  };

  const getContent = (): ReactNode => {
    if (isMobileScreen(windowScreenSize.windowWidth)) {
      return getDefaultLoginPageContent();
    }
    return getAIChatLoginPageContent();
  };

  return <>{getContent()}</>;
}

export default React.memo(LoginSignupTestimony);
